<template>
  <modal
      v-if="modalConfig"
      :arrowConfig="arrowConfig"
      :multiButtons="alertedTerm.suggestions || []"
      :modalStyling="modalStyling"
      :showClose="!isBlock"
      showArrow
      :wrapperClasses="['blacklisted-terms-modal', {['expand']: showHelpInfo}]"
      @action="replaceBlacklistedTerm"
      @close="closeModal">
    <section slot="body" class="blacklisted-terms-message">
      <div class="message">
        we noticed you included the term <span :class="`${restrictionType}-color`">"{{restrictedTerm}}"</span> in your message
      </div>
      <div class="message" v-html="$data[`${restrictionType}Phrase`]"></div>
      <div v-if="showCarrierMessage" class="message">
        messages with this term are often flagged by: <span>{{modalConfig.blacklistedCarriers}}</span>
      </div>
      <div class="message">
        <a
            href="#"
            class="remove-link"
            title="remove alerted term"
            @click.prevent="replaceBlacklistedTerm('')">remove</a> the term <!--ugly indentation to fix weird spacing-->
        <template v-if="alertedTerm.suggestions.length > 0">
          or replace <span :class="`${restrictionType}-color`">"{{restrictedTerm}}"</span> with one of the terms below
        </template>
      </div>
    </section>
    <footer slot="footer" class="blacklisted-terms-footer">
      <sms-replacement-term
          :isBlock="isBlock"
          @closeModal=closeModal
          @replaceBlacklistedTerm="replaceBlacklistedTerm">
      </sms-replacement-term>
      <carrier-restriction-info
          v-if="isBlock"
          :restrictedTerm="restrictedTerm"
          :showHelpInfo="showHelpInfo"
          @setShowHelpInfo="setShowHelpInfo">
      </carrier-restriction-info>
    </footer>
  </modal>
</template>

<script>
  import { mapState } from 'vuex'
  import Modal from '../shared/Modal'
  import CarrierRestrictionInfo from './SmsCarrierRestrictionInfo'
  import SmsReplacementTerm from './SmsReplacementTerm'

  export default {
    props: {
      moduleName: String,
      alertedTerm: Object,
      showModal: Boolean,
    },

    name: 'BlacklistModal',

    components: {
      Modal,
      CarrierRestrictionInfo,
      SmsReplacementTerm
    },

    data() {
      return {
        arrowConfig: {},
        blockPhrase: `Carriers have <span class="block-color">restricted</span> sms deliverability for content with these words. Please re-phrase your selection`,
        infoPhrase: 'Replacing these words could provide increased sms deliverability.',
        modalConfig: null,
        modalStyling: null,
        showHelpInfo: false,
        supportEmail: 'support@springbig.com',
        warningPhrase: 'Carriers have restricted sms deliverability for content with these words. Using them may result in failed delivery of messages.',
      }
    },

    computed: {
      isBlock: function () {
        return this.allowBlockBlacklist && this.restrictionType === 'block'
      },

      restrictedTerm: function () {
        return this.alertedTerm?.phrase || ''
      },

      restrictionType: function () {
        return this.alertedTerm?.blacklist_type
      },

      showCarrierMessage: function () {
        const hasCarriers = this.alertedTerm?.problematic_carriers?.length > 0,
          notMobile = document.documentElement.clientWidth > 640
        return notMobile && hasCarriers
      },

      ...mapState([
        'allowBlockBlacklist'
      ])
    },

    methods: {
      clearModal() {
        this.modalConfig = null
        this.arrowConfig = {}
        document.body.classList.remove("modal-open")

        this.$store.dispatch('DISPLAYING_MODAL', null)
      },

      closeModal() {
        this.$emit('updateIgnoredTerms')
        this.clearModal()
      },

      formatCarriers() {
        const carriers = this.alertedTerm.problematic_carriers.map((c) => c.short_name)
        return carriers.includes('All Carriers') ? 'All Carriers' : carriers.join(', ')
      },

      replaceBlacklistedTerm(newTerm) {
        this.$emit('replaceBlacklistedTerm', newTerm)
        this.clearModal()
      },

      scrollToHighlightTerm() {
        const $highlightContainer = document.querySelector('.js-has-word-highlight')
        $highlightContainer.scrollIntoView(true)
        this.setModal()
      },

      setModal() {
        this.modalConfig = {
          actionButton: this.alertedTerm.suggestions,
          blacklistedTerm: this.alertedTerm.phrase,
          blacklistedCarriers: this.formatCarriers(),
          suggestions: this.alertedTerm.suggestions
        }
        this.$nextTick(() => this.setModalPosition() )
      },

      setModalPosition() {
        let term = document.querySelector(".sms-input.ql-container.ql-snow > div.ql-editor > p > span:not(.ql-label)"),
          termRect = term.getBoundingClientRect(),
          modal = document.querySelector('.modal'),
          modalRect = modal.getBoundingClientRect(),
          viewHeight = document.documentElement.clientHeight,
          viewWidth = document.documentElement.clientWidth,
          modalPadding = 8,
          arrowSize = 5,
          left = termRect.left - (modalRect.width/2) + (termRect.width/2),
          top = termRect.bottom + modalPadding,
          arrowLeft = termRect.left + (termRect.width/2) - modalPadding,
          arrowTop = top - arrowSize,
          maxModalHeightStyles = ''
        let bottom = top + modalRect.height

        this.arrowConfig.arrowClasses = 'up'

        if (left < 0) { // modal goes off left side of screen
          left = modalPadding
        } else if (termRect.left + (modalRect.width/2) > viewWidth) { // modal goes off right side of screen
          left = viewWidth - modalRect.width - modalPadding
        }

        if (bottom > viewHeight) { // modal goes off bottom of screen
          const modalHeightWithArrowAndPadding = bottom + modalPadding + arrowSize,
            viewHeightDifference = modalHeightWithArrowAndPadding - viewHeight,
            inViewModalHeight = modalRect.height - viewHeightDifference
          bottom = top + inViewModalHeight
          maxModalHeightStyles = `max-height: ${inViewModalHeight}px; overflow: auto;`
        }

        this.modalStyling = `left: ${left}px; top: ${top}px; ${maxModalHeightStyles}`
        this.arrowConfig.arrowStyling = `left: ${arrowLeft}px; top: ${arrowTop}px;`
        document.body.classList.add("modal-open")

        const modalMetadata = {
          name: 'BlacklistModal',
          module: this.moduleName,
          position: {
            top,
            bottom,
            left,
            right: left + modalRect.width
          },
          dimension: {
            width: modalRect.width,
            height: bottom - top
          }
        }

        this.$store.dispatch('DISPLAYING_MODAL', modalMetadata)
      },

      setShowHelpInfo(showHelpInfo) {
        this.showHelpInfo = showHelpInfo
      },
    },

    watch: {
      alertedTerm: function () {
        this.showModal && this.scrollToHighlightTerm()
      },

      showHelpInfo: function () {
        this.$nextTick(() => this.setModalPosition() )
      },
    }
  }
</script>